import { filterState } from './filter.svelte'
import { addNotification } from '$lib/stores'
import { NotificationType } from '$types/notification'
import { get } from 'svelte/store'
import { t } from '$i18n/i18n'
import { LotState } from '$types/enums'
import {
	appState,
	requestCompletedLots,
	requestDiscardedLots
} from '$mapapp/stores/appstate.svelte'
import { getUserPreference, setUserPreference } from '$utils/user_preferences'
import { filters } from './filter.config.svelte'

/**
 * registry ids may be dotted (e.g. `popVisibility.external`). These helpers
 * resolve a path on either the BE `FilterProfileConfig` blob or `filterState`,
 * since both intentionally share the same nested shape.
 */
export function readPath(obj: unknown, path: string): unknown {
	let current: unknown = obj
	for (const part of path.split('.')) {
		if (current == null || typeof current !== 'object') {
			return undefined
		}
		current = (current as Record<string, unknown>)[part]
	}
	return current
}

export function writePath(obj: unknown, path: string, value: unknown): void {
	const parts = path.split('.')
	let current = obj as Record<string, unknown>
	for (let i = 0; i < parts.length - 1; i++) {
		const part = parts[i]
		current[part] ??= {}
		current = current[part] as Record<string, unknown>
	}
	current[parts.at(-1)!] = value
}

const DEFAULT_MAPLIBRE_FILTER_KEY = 'maplibre-filter'
export const MATCH_MAPLIBRE_FILTER_KEY = 'maplibre-match-filter'

type FilterProfileMode = 'default' | 'match'

/**
 * in-memory cache of last successfully fetched config per mode.
 * Used by applyCachedProfile() for synchronous filterState writes on FSM transitions,
 * avoiding the async-fetch flash that would otherwise let stale filters paint for a frame.
 */
let _lastDefaultProfile: FilterProfileConfig | null = null
let _lastMatchProfile: FilterProfileConfig | null = null

function modeFromUrl(key: string): FilterProfileMode {
	return key === MATCH_MAPLIBRE_FILTER_KEY ? 'match' : 'default'
}

function setCache(mode: FilterProfileMode, config: FilterProfileConfig) {
	if (mode === 'match') _lastMatchProfile = config
	else _lastDefaultProfile = config
}

function getCache(mode: FilterProfileMode): FilterProfileConfig | null {
	return mode === 'match' ? _lastMatchProfile : _lastDefaultProfile
}

/**
 * BE persistence shape. field names mirror `FilterDimension.id`s in
 * `filter.config.svelte.ts` — every key here corresponds to a
 * `persisted: true` dimension, and vice versa.
 *
 * `allLotTypes` / `allPopTypes` are legacy derived flags kept for
 * forward-compatibility with consumers outside the SPA. despite their name
 * they encode `some-are-on`, not `all-are-on` — pre-refactor semantics retained.
 */
type FilterProfileConfig = {
	popTypes?: Record<string, boolean>
	popMaintenance?: Record<string, boolean>
	lotTypes?: Record<string, boolean>
	lotStates?: Record<string, boolean>
	popVisibility?: { external?: boolean; internal?: boolean }
	groupLots?: boolean
	showPublished?: boolean
	showOwnProjects?: boolean
	showRequestedByMM?: boolean
	allLotTypes?: boolean
	allPopTypes?: boolean
}

/**
 * snapshot of the live filterState taken just before entering matching mode.
 * restored by restorePreMatchSnapshot() on _exit so unsaved in-session filter
 * changes survive the matching session.
 */
let _preMatchSnapshot: FilterProfileConfig | null = null

/**
 * one-shot flag: set by restorePreMatchSnapshot() so the async
 * fetchFilterPreference(default) that fires from matching._exit caches the
 * BE profile but skips overwriting the just-restored snapshot.
 */
let _skipNextDefaultApply = false

/**
 * idempotency guards for the lazy BE loads. plain module vars — these are
 * only ever read/written by the guards themselves, not by the UI, so no
 * reason to make them reactive.
 */
let _completedLoaded = false
let _discardedLoaded = false

/**
 * opt-in lazy load: COMPLETED / DISCARDED lots are NOT shipped by the BE on
 * initial map load, and the product decision is that "All" should not
 * silently pull them either — only the explicit per-checkbox click and
 * applying a saved profile with the state already on count as opt-in.
 */
export function loadCompletedLotsOnce() {
	if (_completedLoaded) return
	_completedLoaded = true
	requestCompletedLots()
}

export function loadDiscardedLotsOnce() {
	if (_discardedLoaded) return
	_discardedLoaded = true
	requestDiscardedLots()
}

/**
 * apply a BE config blob to `filterState`, walking the registry.
 *
 *  - visibility-gated dimensions (e.g. SaaS-only `popVisibility.*`) are
 *    skipped when `visible()` returns false — non-SaaS users keep their
 *    in-memory defaults regardless of what the blob contains.
 *  - multi-value dimensions are merged per-key, not replaced wholesale, so
 *    options added to the registry after a preference was saved retain
 *    their default state instead of disappearing from the panel.
 *  - unknown keys on the blob are silently ignored (forward-compat).
 *  - missing keys keep in-memory defaults (backward-compat).
 *
 * post-walk, the COMPLETED / DISCARDED lazy-loads are triggered if the
 * saved profile had them on — applying a saved profile counts as opt-in
 * for those states.
 */
function applyConfigToFilterState(config: FilterProfileConfig | null) {
	if (!config) return

	for (const dim of filters) {
		if (!dim.persisted) continue
		if (dim.visible && !dim.visible()) continue
		if (dim.readOnly?.()) continue

		const value = readPath(config, dim.id)
		if (value === undefined) continue

		if (dim.kind === 'multi') {
			/**
			 * per-key merge — preserves defaults for options the saved blob
			 * pre-dates (e.g. new pop types added since last save).
			 */
			const target = readPath(filterState, dim.id) as Record<string, boolean> | undefined
			if (!target || typeof value !== 'object' || value === null) continue
			for (const [k, v] of Object.entries(value as Record<string, boolean>)) {
				target[k] = v
			}
		} else {
			writePath(filterState, dim.id, value)
		}
	}

	if (filterState.lotStates[LotState.COMPLETED]) loadCompletedLotsOnce()
	if (filterState.lotStates[LotState.DISCARDED]) loadDiscardedLotsOnce()
}

/**
 * build a `FilterProfileConfig` for save. walks the registry; skips
 * dimensions whose `visible()` returns false, so non-SaaS users don't
 * persist SaaS-only flags (and vice versa).
 *
 * `allLotTypes` / `allPopTypes` are derived legacy fields retained from the
 * pre-registry blob shape — semantically `some-are-on`, kept under their
 * historical names for any external consumer that still reads them.
 */
function buildPreferenceConfig(): FilterProfileConfig {
	const out: FilterProfileConfig = {}
	for (const dim of filters) {
		if (!dim.persisted) continue
		if (dim.visible && !dim.visible()) continue
		if (dim.readOnly?.()) continue
		const value = readPath(filterState, dim.id)
		if (value === undefined) continue
		writePath(out, dim.id, value)
	}
	out.allLotTypes = Object.values(filterState.lotTypes).some((v) => v)
	out.allPopTypes = Object.values(filterState.popTypes).some((v) => v)
	return out
}

/**
 * synchronously apply the cached profile for the given mode to filterState.
 * no-op on cache miss (cold first transition; the async fetch follows).
 * called from matching FSM _enter / _exit before the async refresh kicks off.
 */
export function applyCachedProfile(mode: FilterProfileMode): boolean {
	const cached = getCache(mode)
	if (!cached) return false
	applyConfigToFilterState(cached)
	return true
}

/**
 * capture the current live filterState as a pre-matching snapshot so that
 * unsaved in-session filter changes survive the matching session.
 * must be called from matching._enter BEFORE applyCachedProfile('match').
 *
 * buildPreferenceConfig() assigns multi-value dimensions (lotTypes, lotStates,
 * popTypes, popMaintenance) by reference — they ARE the Svelte reactive proxies.
 * structuredClone rejects those proxies, so each record field is spread manually
 * to produce an independent plain-object copy.
 */
export function capturePreMatchSnapshot(): void {
	const config = buildPreferenceConfig()
	_preMatchSnapshot = { ...config }
	if (config.lotTypes) _preMatchSnapshot.lotTypes = { ...config.lotTypes }
	if (config.lotStates) _preMatchSnapshot.lotStates = { ...config.lotStates }
	if (config.popTypes) _preMatchSnapshot.popTypes = { ...config.popTypes }
	if (config.popMaintenance) _preMatchSnapshot.popMaintenance = { ...config.popMaintenance }
}

/**
 * restore the snapshot captured by capturePreMatchSnapshot().
 * called from matching._exit instead of applyCachedProfile('default').
 *
 * if a snapshot exists, it is applied synchronously and a one-shot flag is
 * set, falls back to applyCachedProfile('default') when no snapshot is available
 * (e.g. cold start where _enter never ran).
 */
export function restorePreMatchSnapshot(): boolean {
	if (!_preMatchSnapshot) {
		return applyCachedProfile('default')
	}
	applyConfigToFilterState(_preMatchSnapshot)
	_preMatchSnapshot = null
	_skipNextDefaultApply = true
	return true
}

export async function fetchFilterPreference(preferenceKey: string = DEFAULT_MAPLIBRE_FILTER_KEY) {
	const mode = modeFromUrl(preferenceKey)
	try {
		const config: FilterProfileConfig | null =
			await getUserPreference<FilterProfileConfig>(preferenceKey)

		if (!config) {
			console.warn('No filter preference found for mode', mode)
			return
		}

		// always cache, even if we skip applying — primes the cache for next transition
		setCache(mode, config)

		/**
		 * late-arrival gate: only write to filterState if the fetched mode still matches
		 * the live FSM state. prevents a slow default fetch (e.g. FilterControl.onMount)
		 * from clobbering a freshly-loaded matching profile when the user lands on
		 * `?match=...` and the matching FSM enter races ahead of the default fetch.
		 */
		const isMatching = appState.isMatching
		if ((mode === 'match') !== isMatching) return

		/**
		 * one-shot guard: skip apply when matching._exit just restored a pre-match
		 * snapshot; the cache above is already updated for the next session.
		 */
		if (mode === 'default' && _skipNextDefaultApply) {
			_skipNextDefaultApply = false
			return
		}

		applyConfigToFilterState(config)
	} catch (error) {
		console.error('Error fetching filter preferences:', error)
	}
}

export async function saveFilterPreference(preferenceKey: string = DEFAULT_MAPLIBRE_FILTER_KEY) {
	try {
		const configuration = buildPreferenceConfig()
		await setUserPreference(preferenceKey, configuration)

		/**
		 * write-through cache: keep the in-memory profile fresh so the next FSM
		 * transition's applyCachedProfile() reflects what the user just saved.
		 */
		const saveMode: FilterProfileMode =
			preferenceKey === MATCH_MAPLIBRE_FILTER_KEY ? 'match' : 'default'
		setCache(saveMode, configuration)

		addNotification({
			message: get(t)('UI.map.saveFilterPreferences'),
			type: NotificationType.SUCCESS,
			timeout: 4000,
			dismissible: false
		})
	} catch (error) {
		console.error('Error saving filter preferences:', error)
		addNotification({
			message: get(t)('UI.map.saveFilterPreferences.error'),
			type: NotificationType.ERROR,
			timeout: 8000,
			dismissible: true
		})
	}
}
